import React, { useEffect, useState } from "react";
import { Text, Flex } from "@chakra-ui/react";
import { stagger, useAnimate, useInView } from "framer-motion";
import * as Sentry from "@sentry/nextjs";
import { AddBoxIcon, PlusIcon, TraitIcon } from "ui/icons";
import { DecorativeBackgroundImage } from "components/images";
import bgImage from "../../../../public/assets/platform/customer-studio/traits-bg.png";
import { Row } from "./row";
import { Container } from "./container";
import { FilterOption } from "./filter-option";
import { FilterButton } from "./filter-button";
import { EventIcon } from "./event-icon";

const defaultDelay = 0.75;

const fadeIn = { opacity: 1, scale: 1 };
const fadeOut = { opacity: 0, scale: 0.98 };
const resetTiming = { duration: 0.05 };

function useTraitsAnimation(isActive: boolean, delay = defaultDelay) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      try {
        await animate(
          isActive
            ? [
                // Animation sequence
                [".traits-fade-in", fadeIn, { at: `+${delay}`, type: "spring", delay: stagger(0.35) }],
              ]
            : [
                // Reset
                [".traits-fade-in", fadeOut, resetTiming],
              ],
        );
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [isActive, delay, animate]);

  return scope;
}

export function TraitsAnimation({ delay = defaultDelay }: { delay?: number }) {
  const [animationActive, setAnimationActive] = useState(false);
  const scope = useTraitsAnimation(animationActive, delay);
  const isInView = useInView(scope, { amount: 0.5 });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Flex
      ref={scope}
      align="center"
      bg="new.blue.150"
      boxSize="100%"
      color="brand.text.light"
      pos="relative"
      pl={{ base: "small", xl: "big" }}
      py={{ base: 6, lg: 7 }}
    >
      <DecorativeBackgroundImage priority src={bgImage} />

      <Flex
        align="start"
        bg="product.gray.50"
        borderLeftRadius="smaller"
        boxShadow="lg"
        direction="column"
        flex={1}
        justify="start"
        maxH="340px"
        mx="auto"
        pos="relative"
        pt={{ base: "smallest", md: "small" }}
        pl={{ base: "smaller", md: "smallest", xl: "smaller" }}
        pb={{ base: "small", md: "big", xl: "bigger" }}
      >
        <Flex align="start" direction="column" gap="smallest" w="100%">
          <Text textStyle="caption1" fontWeight="semibold">
            Define your audience
          </Text>

          <Container>
            <Row>
              <Text as="span" color="product.gray.600">
                Has
              </Text>
              <TraitIcon boxSize="24px" color="new.yellow.300" />
              <Text as="span">Trait calculation</Text>
            </Row>

            <Row>
              <FilterOption>Sum</FilterOption>

              <FilterOption className="traits-fade-in" opacity={0}>
                Price
              </FilterOption>

              <Text as="span" className="traits-fade-in" color="product.gray.600" whiteSpace="nowrap" opacity={0}>
                of events in
              </Text>

              <FilterOption className="traits-fade-in" opacity={0}>
                <EventIcon boxSize={{ base: 2.5, lg: 3, xl: 4 }} />
                Order Completed
              </FilterOption>

              <FilterOption className="traits-fade-in" label="greater than" opacity={0}>
                50th percentile
              </FilterOption>
            </Row>

            <Row>
              <AddBoxIcon boxSize="12px" color="brand.textSecondary.light" ml={2} />
              <Text color="brand.textSecondary.light">Where...</Text>
            </Row>
          </Container>

          <FilterButton textTransform="uppercase">and</FilterButton>

          <FilterButton>
            <PlusIcon boxSize={{ base: "10px", lg: "12px" }} />
            Add filter
          </FilterButton>
        </Flex>
      </Flex>
    </Flex>
  );
}
